import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Slider from "src/components/slider";
import { fetchOpportunities, invalidateIntegrationOpportunities } from "src/redux/actions/opportunities";
import { Conversation } from "src/types/conversations";
import { RootState } from "src/redux/store";

import InfiniteScroll from "src/components/infiniteScroll";
import { Checkbox } from "src/components/input";
import Actions from "src/components/actionbar";
import Button from "src/components/button";
import { DealLoader } from "../loaders";
import EmptyMessage from "src/components/emptyMessage";

import cx from "classnames";
import _ from "lodash";
import styles from "./mapDeals.module.scss";

interface IMapDeals {
  show: boolean;
  onClose: () => void;
  conversation: Conversation;
  onSelect: (opportunityId: string) => void;
}

const MapDeals: React.FC<IMapDeals> = ({
  show,
  onClose,
  conversation,
  onSelect,
}) => {
  //constants
  const dispatch = useDispatch();

  //state
  const [query, setQuery] = React.useState<string | null>("");
  const [selectedOpportunity, setSelectedOpportunity] = React.useState<
    string | null
  >(null);
  const [updating, setUpdating] = React.useState(false);

  //selectors
  const { pagination, items, loading } = useSelector(
    (state: RootState) => state.opportunities,
  );

  //side effects
  useEffect(() => {
    if (show) {
      if (loading) return;
      dispatch(fetchOpportunities({ query: "", page: 0 }));
    } else {
      setQuery("");
      setUpdating(false);
      setSelectedOpportunity(null);
    }
  }, [show]);

  useEffect(() => {
    if (
      show &&
      _.size(conversation.opportunities) > 0 &&
      _.find(conversation.opportunities, { opportunityId: selectedOpportunity })
    ) {
      setUpdating(false);
      onClose();
    }
  }, [conversation.opportunities]);

  //handlers
  const handleQueryChange = (query: string | null) => {

    setQuery(query);
    if(query === null) {
      dispatch(invalidateIntegrationOpportunities());
    }
    
    if (loading) return;
    if (!query || query === "") {
      dispatch(fetchOpportunities({ query: "", page: 0 }));
    } else {
      dispatch(fetchOpportunities({ query, page: 0 }));
    }
  };

  const handleMapDeal = () => {
    if (selectedOpportunity) {
      setUpdating(true);
      onSelect(selectedOpportunity);
    }
  };

  const hasMore = _.get(pagination, "nextPage", 0 ) > 0 || pagination.currentPage < pagination.totalPages - 1

  //render
  return (
    <Slider.Dialog width={"25rem"} show={show} onClose={onClose}>
      <Slider.Header className={styles.header}>
        <div className={styles.titleContainer}>
          <div className={styles.title}>map deals</div>
          <div className={styles.subTitle}>
            Pick the deal you want emotionally attached to this share.
          </div>
        </div>
        <i className="paperflite-close" onClick={onClose} />
      </Slider.Header>

      <Slider.Body className={styles.body}>
        <div className={styles.searchContainer}>
          <Actions.Search query={query} onSearch={handleQueryChange} />
        </div>

        {!loading && _.size(items) === 0 && (
          <EmptyMessage
            image="/images/noDeals.svg"
            message="No Records Found"
            className={styles.emptyMessage}
          />
        )}

        <section className={styles.opportunities}>
          <InfiniteScroll
            className={styles.list}
            hasMore={hasMore}
            loader={() => (
              <DealLoader count={pagination.currentPage > 0 ? 1 : 3} />
            )}
            loading={loading}
            loadMore={() => {
              if (loading) return;
              dispatch(
                fetchOpportunities({
                  query: query || "",
                  page: pagination.currentPage + 1,
                  nextPage: _.get(pagination, "nextPage", 0 )
                }),
              );
            }}
          >
            {items.map((opportunity) => (
              <div
                key={opportunity.id}
                onClick={() => setSelectedOpportunity(opportunity.id)}
                className={cx(styles.opportunity, {
                  [styles.selected]: selectedOpportunity === opportunity.id,
                })}
              >
                <div className={styles.title}>{opportunity.name}</div>
                <Checkbox
                  type="radio"
                  checked={selectedOpportunity === opportunity.id}
                  onChange={() => setSelectedOpportunity(opportunity.id)}
                />
              </div>
            ))}
          </InfiniteScroll>
        </section>
      </Slider.Body>

      {_.size(items) > 0 && (
        <Slider.Actions>
          <Button
            text={updating ? "mapping" : "confirm"}
            disabled={!selectedOpportunity}
            icon="paperflite-tick"
            variant="primary"
            onClick={handleMapDeal}
          />
          <Button onClick={onClose} text="cancel" />
        </Slider.Actions>
      )}
    </Slider.Dialog>
  );
};

export default MapDeals;
